import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import api from '@/api';
import {
  isErrorCode,
  retryUnlessNotInYourVersion,
  yourVersionCourseSlug,
} from '@/api/errors';
import { TopicContent } from '@/courses/topic/TopicContent';
import { useCourseContext } from '~/contexts/courseContext';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { sameSlug } from '~/utils/slug';

export const Route = createFileRoute('/_app/_course/topic/$topicSlug')({
  component: TopicScreen,
});

function TopicScreen() {
  const { topicSlug } = Route.useParams();
  const { setSelectedLessonSlug, setExpandedLessons, setSelectedTopicSlug } =
    useCourseContext();
  const { me, isStudent } = useCurrentUser();
  const navigate = useNavigate();
  const language = me?.language ?? 'EN';

  // `topic_by_slug` is the ungated resolver and carries the lesson and course
  // ids and `course_slug` that the 403 redirect below needs. Taking them from
  // the gated `topicDetail` left a refused student on a blank page.
  const { data: resolved, error: resolveError } = api.topicBySlug.useQuery(
    { path: { slug: topicSlug }, query: { language } },
    { retry: retryUnlessNotInYourVersion },
  );

  const topicId = resolved?.id;
  const lessonId = resolved?.lesson_id;
  const courseId = resolved?.course_id;
  const lessonSlug = resolved?.lesson_slug ?? '';
  // The stored slug, not the route param: a migrated slug can be stored
  // percent-encoded, and the sidebar matches against the stored form.
  const selectedSlug = resolved?.slug ?? topicSlug;

  const { data: topicDetail, error: topicDetailError } =
    api.topicDetail.useQuery(
      { path: { topic_id: topicId ?? 0 } },
      { enabled: topicId != null },
    );

  const { data: lessonDetail } = api.lessonDetail.useQuery(
    { path: { lesson_id: lessonId ?? 0 } },
    { enabled: lessonId != null },
  );

  const { data: topicsData } = api.lessonTopics.useQuery(
    { path: { lesson_id: lessonId ?? 0 }, query: { limit: 50 } },
    { enabled: lessonId != null },
  );

  const { data: courseLessons } = api.courseLessons.useQuery(
    { path: { course_id: courseId ?? 0 }, query: { limit: 50 } },
    { enabled: courseId != null },
  );

  const { data: courseDetail } = api.courseDetail.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: viewerState } = api.courseViewerState.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: courseProgress } = api.courseProgress.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null && isStudent },
  );

  const { mutateAsync: markComplete } = api.topicComplete.useMutation();
  const { mutateAsync: submitTopicQuiz } = api.submitTopicQuiz.useMutation();

  // The inline topic-quiz panel's copy, resolved per language by the API.
  const { data: topicQuizResult, isLoading: topicQuizResultLoading } =
    api.topicQuizResult.useQuery(
      { path: { topic_id: topicId ?? 0 } },
      { enabled: topicId != null },
    );

  useEffect(() => {
    if (!lessonSlug) return;
    setSelectedLessonSlug(lessonSlug);
    setExpandedLessons((prev) =>
      prev.includes(lessonSlug) ? prev : [...prev, lessonSlug],
    );
    setSelectedTopicSlug(selectedSlug);
  }, [
    lessonSlug,
    selectedSlug,
    setSelectedLessonSlug,
    setExpandedLessons,
    setSelectedTopicSlug,
  ]);

  const courseSlug = resolved?.course_slug;

  // A no-access topic redirects to the parent course, as in legacy.
  // `topic_detail` is the gated read; the viewer-state read covers the case
  // where the two disagree.
  const denied =
    viewerState?.has_access === false ||
    isErrorCode(topicDetailError, 'course_access_required');
  if (denied && courseSlug) {
    return (
      <Navigate to="/courses/$courseSlug/" params={{ courseSlug }} replace />
    );
  }

  // An old link: the viewer's version of the course has no such topic.
  const versionCourseSlug = yourVersionCourseSlug(resolveError);
  if (versionCourseSlug) {
    return (
      <Navigate
        to="/courses/$courseSlug/"
        params={{ courseSlug: versionCourseSlug }}
        replace
      />
    );
  }

  // An old link to the same topic in another version: show the viewer's
  // version's address, which the sidebar and next links use.
  if (resolved && !sameSlug(resolved.slug, topicSlug)) {
    return (
      <Navigate
        to="/topic/$topicSlug/"
        params={{ topicSlug: resolved.slug }}
        replace
      />
    );
  }

  const waitingForQuizState = topicId != null && topicQuizResultLoading;

  if (!topicDetail || !lessonDetail || waitingForQuizState) {
    return null;
  }

  const lessonProgress = courseProgress
    ? courseProgress[String(lessonId)]
    : undefined;

  const lessonProgressPct =
    lessonProgress && lessonProgress.topics_total > 0
      ? Math.round(
          (lessonProgress.topics_completed / lessonProgress.topics_total) * 100,
        )
      : 0;

  // Set membership, not `topic.order <= topics_completed`: `order` is
  // course-scoped while the count is per lesson, so that comparison marked the
  // wrong topics.
  const isCompleted =
    lessonProgress?.completed_topic_ids.includes(topicDetail.id) ?? false;

  const handleSubmitQuiz = async (
    answers: Record<string, string | string[] | boolean[]>,
  ) => {
    await submitTopicQuiz({
      path: { topic_id: topicId ?? 0 },
      body: { answers: answers as any },
    });
  };

  // Navigation only: a topic-scoped quiz submit already marks the topic
  // complete server-side.
  const advance = async () => {
    const topics = topicsData?.data ?? [];
    const nextTopic = topics.find((t) => t.order === topicDetail.order + 1);

    if (nextTopic) {
      navigate({
        to: '/topic/$topicSlug/',
        params: { topicSlug: nextTopic.slug },
      });
      return;
    }

    // `has_final_quiz`, not a `lessonQuestions` probe: that read is
    // quiz-locked, and on the last topic the topics aren't all complete yet, so
    // the probe would 403 and send the student past the quiz.
    if (lessonDetail.has_final_quiz && lessonSlug) {
      navigate({
        to: '/lessons/$lessonSlug/quiz/',
        params: { lessonSlug },
      });
      return;
    }

    const nextLesson = (courseLessons?.data ?? []).find(
      (l) => l.order === lessonDetail.order + 1,
    );
    if (nextLesson) {
      navigate({
        to: '/lessons/$lessonSlug/',
        params: { lessonSlug: nextLesson.slug },
      });
      return;
    }

    if (courseSlug) {
      navigate({
        to: '/courses/$courseSlug/',
        params: { courseSlug },
      });
    }
  };

  const handleContinue = async () => {
    await markComplete({ path: { topic_id: topicId ?? 0 } });
    await advance();
  };

  return (
    <TopicContent
      courseTitle={courseDetail?.title ?? ''}
      courseSlug={courseSlug ?? ''}
      lessonId={lessonId}
      lessonTitle={lessonDetail.title}
      lessonSlug={lessonSlug}
      topicTitle={topicDetail.title}
      topicSlug={topicSlug}
      htmlContent={topicDetail.content}
      lessonNumber={lessonDetail.index + 1}
      topicNumber={topicDetail.index + 1}
      thoughtQuestions={topicDetail.questions}
      quizResult={topicQuizResult}
      memberId={me?.id?.toString()}
      isStudent={isStudent}
      lessonProgress={lessonProgressPct}
      isCompleted={isCompleted}
      onContinue={handleContinue}
      onQuizSubmitted={advance}
      onSubmitQuiz={handleSubmitQuiz}
    />
  );
}
